import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TableViewHeader } from '@services/ui-services/models';

export interface SortConfig {
  [column: string]: 'asc' | 'desc' | 'default';
}

export interface ClientSideSortingConfig<T> {
  /** Function to extract sortable value from the data item */
  getSortValue: (item: T, column: string) => any;
  /** Initial sort configuration */
  initialSort?: SortConfig;
  /** Whether to store sorted data for future reference */
  cacheResults?: boolean;
}

export interface SortableDataStream<T> {
  /** Observable stream of sorted data */
  data$: Observable<T[]>;
  /** Function to update sort configuration */
  updateSort: (sortConfig: SortConfig) => void;
}

export interface AutoSortingConfig<T> {
  /** Table headers containing sortable column configuration */
  tableHeaders: TableViewHeader[];
  /** Initial sort configuration */
  initialSort?: SortConfig;
  /** Custom sort value extractors for specific columns */
  customSortValues?: Record<string, (item: T) => any>;
  /** Whether to store sorted data for future reference */
  cacheResults?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ClientSideSortingService {
  /**
   * Creates a sortable data stream with proper type safety and immutability
   * Returns an object with the data observable and update function separated
   */
  createSortableDataStream<T>(
    initialData: T[],
    config: ClientSideSortingConfig<T>
  ): SortableDataStream<T> {
    const sortSubject = new BehaviorSubject<SortConfig>(config.initialSort || {});
    const originalData = [...initialData];

    const data$ = sortSubject.pipe(
      map(sortConfig => this.applySorting(originalData, sortConfig, config.getSortValue))
    );

    const updateSort = (sortConfig: SortConfig) => {
      sortSubject.next(sortConfig);
    };

    return {
      data$,
      updateSort,
    };
  }

  /**
   * Creates a sortable data stream using auto-sorting configuration
   */
  createAutoSortableDataStream<T>(
    initialData: T[],
    config: AutoSortingConfig<T>
  ): SortableDataStream<T> {
    const sortSubject = new BehaviorSubject<SortConfig>(config.initialSort || {});
    const originalData = [...initialData];
    const getSortValue = this.createAutoSortValueExtractor(config);

    const data$ = sortSubject.pipe(
      map(sortConfig => this.applySorting(originalData, sortConfig, getSortValue))
    );

    const updateSort = (sortConfig: SortConfig) => {
      sortSubject.next(sortConfig);
    };

    return {
      data$,
      updateSort,
    };
  }

  /**
   * Simple utility method for one-time sorting
   */
  sortData<T>(
    data: T[],
    sortConfig: SortConfig,
    getSortValue: (item: T, column: string) => any
  ): T[] {
    return this.applySorting(data, sortConfig, getSortValue);
  }

  /**
   * Auto-sorting method that derives sort logic from table headers
   */
  sortDataAuto<T>(data: T[], sortConfig: SortConfig, config: AutoSortingConfig<T>): T[] {
    const getSortValue = this.createAutoSortValueExtractor(config);
    return this.applySorting(data, sortConfig, getSortValue);
  }

  /**
   * Creates a sort value extractor based on table headers and custom extractors
   */
  private createAutoSortValueExtractor<T>(
    config: AutoSortingConfig<T>
  ): (item: T, column: string) => any {
    const { tableHeaders, customSortValues = {} } = config;

    // Create a map of sortable columns from table headers
    const sortableColumns = new Map<string, TableViewHeader>();
    tableHeaders
      .filter(header => header.sortable && header.column)
      .forEach(header => {
        if (header.column) {
          sortableColumns.set(header.column, header);
        }
      });

    return (item: T, column: string): any => {
      // Check if there's a custom sort value extractor for this column
      const customExtractor = customSortValues[column];
      if (customExtractor) {
        return customExtractor(item);
      }

      // Use the column value directly from the data
      const value = (item as any)[column];

      if (value === undefined || value === null) {
        return '';
      }

      // Get the header configuration for type-specific handling
      const header = sortableColumns.get(column);
      if (header) {
        switch (header.type) {
          case 'string':
          case 'text':
          case 'product_details':
            return String(value).toLowerCase();
          case 'number':
          case 'currency':
            return Number(value) || 0;
          case 'date':
            return value instanceof Date ? value : new Date(value);
          case 'boolean':
            return Boolean(value);
          default:
            // For unknown types, try to handle intelligently
            if (typeof value === 'string') return value.toLowerCase();
            if (typeof value === 'number') return value;
            if (value instanceof Date) return value;
            return String(value).toLowerCase();
        }
      }

      // Fallback: handle by detected type
      if (typeof value === 'string') return value.toLowerCase();
      if (typeof value === 'number') return value;
      if (value instanceof Date) return value;
      return String(value).toLowerCase();
    };
  }

  private applySorting<T>(
    data: T[],
    sortConfig: SortConfig,
    getSortValue: (item: T, column: string) => any
  ): T[] {
    if (!data || data.length === 0) return data;

    // Get active sort columns (not 'default')
    const activeSorts = Object.entries(sortConfig).filter(
      ([, direction]) => direction !== 'default'
    );

    if (activeSorts.length === 0) {
      return [...data]; // Return copy of original data
    }

    return [...data].sort((a, b) => {
      for (const [column, direction] of activeSorts) {
        const aValue = getSortValue(a, column);
        const bValue = getSortValue(b, column);

        let comparison = 0;

        // Handle different data types
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          comparison = aValue.localeCompare(bValue, undefined, { numeric: true });
        } else if (typeof aValue === 'number' && typeof bValue === 'number') {
          comparison = aValue - bValue;
        } else if (aValue instanceof Date && bValue instanceof Date) {
          comparison = aValue.getTime() - bValue.getTime();
        } else {
          // Fallback to string comparison
          comparison = String(aValue).localeCompare(String(bValue), undefined, { numeric: true });
        }

        if (comparison !== 0) {
          return direction === 'asc' ? comparison : -comparison;
        }
      }

      return 0;
    });
  }
}
